import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatINR, formatDate, methodLabel } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Loader2, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth.jsx";
import EditPaymentDialog from "../components/EditPaymentDialog";

export default function PaymentsPage() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({
    service_type: "all",
    method: "all",
    date_from: "",
    date_to: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.service_type !== "all") params.service_type = filters.service_type;
      if (filters.method !== "all") params.method = filters.method;
      if (filters.date_from) params.date_from = new Date(filters.date_from).toISOString();
      if (filters.date_to) {
        const d = new Date(filters.date_to);
        d.setDate(d.getDate() + 1);
        params.date_to = d.toISOString();
      }
      const { data } = await api.get("/payments", { params });
      setPayments(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const remove = async (id) => {
    if (!window.confirm("Delete this payment? The bill paid amount and customer balance will be reversed automatically.")) return;
    try {
      await api.delete(`/payments/${id}`);
      toast.success("Payment deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const totals = payments.reduce(
    (acc, p) => {
      acc.total += p.amount;
      acc[p.service_type] = (acc[p.service_type] || 0) + p.amount;
      acc.byMethod[p.method] = (acc.byMethod[p.method] || 0) + p.amount;
      return acc;
    },
    { total: 0, internet: 0, cable: 0, byMethod: { cash: 0, gpay: 0, phonepe: 0 } },
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">Payments</h1>
        <p className="text-gray-500 mt-1 text-sm">Transaction history across all collectors and methods.</p>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label>Service</Label>
            <Select value={filters.service_type} onValueChange={(v) => setFilters({ ...filters, service_type: v })}>
              <SelectTrigger data-testid="payments-service-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="internet">Internet</SelectItem>
                <SelectItem value="cable">Cable TV</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Method</Label>
            <Select value={filters.method} onValueChange={(v) => setFilters({ ...filters, method: v })}>
              <SelectTrigger data-testid="payments-method-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="gpay">GPay</SelectItem>
                <SelectItem value="phonepe">PhonePe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <Input data-testid="payments-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div>
            <Label>To</Label>
            <Input data-testid="payments-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">Total</div><div className="pds-stat-num text-2xl mt-1">{formatINR(totals.total)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">Internet</div><div className="pds-stat-num text-2xl mt-1">{formatINR(totals.internet)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">Cable</div><div className="pds-stat-num text-2xl mt-1">{formatINR(totals.cable)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">Cash</div><div className="pds-stat-num text-xl mt-1">{formatINR(totals.byMethod.cash || 0)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">GPay + PhonePe</div><div className="pds-stat-num text-xl mt-1">{formatINR((totals.byMethod.gpay || 0) + (totals.byMethod.phonepe || 0))}</div></Card>
      </div>

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table data-testid="payments-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Bill No</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Collector</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {user?.role === "admin" && <TableHead className="text-right"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={user?.role === "admin" ? 9 : 8} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
              )}
              {!loading && payments.length === 0 && (
                <TableRow><TableCell colSpan={user?.role === "admin" ? 9 : 8} className="text-center text-gray-500 py-6">No transactions.</TableCell></TableRow>
              )}
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-sm">{formatDate(p.collected_at)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{p.customer_name}</div>
                    <div className="text-xs text-gray-500">{p.customer_code}</div>
                  </TableCell>
                  <TableCell className="capitalize">{p.service_type === "cable" ? "Cable TV" : "Internet"}</TableCell>
                  <TableCell><span className="status-chip status-active">{methodLabel(p.method)}</span></TableCell>
                  <TableCell className="font-mono text-sm">{p.bill_no || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-sm text-gray-700 max-w-[220px] truncate" title={p.online_note}>{p.online_note || "—"}</TableCell>
                  <TableCell className="text-sm">{p.collected_by_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{formatINR(p.amount)}</TableCell>
                  {user?.role === "admin" && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          data-testid={`edit-payment-${p.id}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => setEditing(p)}
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4 text-gray-600" />
                        </Button>
                        <Button
                          data-testid={`delete-payment-${p.id}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => remove(p.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <EditPaymentDialog
        payment={editing}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onDone={() => { setEditing(null); load(); }}
      />
    </div>
  );
}
